import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  // Base styles + tablet touch feedback: active:scale for press confirmation, transition-all for smooth feedback
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98] tablet:active:scale-95",
  {
    variants: {
      variant: {
        // Tablet: add shadow for depth perception, making buttons look more "pressable"
        default: "bg-primary text-primary-foreground hover:bg-primary/90 tablet:shadow-md tablet:hover:shadow-lg tablet:active:shadow-sm",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 tablet:shadow-md tablet:hover:shadow-lg tablet:active:shadow-sm",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground tablet:shadow-sm tablet:hover:shadow-md tablet:active:shadow-none tablet:border-2",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 tablet:shadow-md tablet:hover:shadow-lg tablet:active:shadow-sm",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        // Kitchen-optimized sizes for tablet/touch use
        xl: "h-12 rounded-md px-6 text-base [&_svg]:size-5",
        kitchen: "h-14 rounded-xl px-6 text-kitchen-lg font-semibold [&_svg]:size-6",
        "icon-lg": "h-12 w-12 [&_svg]:size-5",
        "icon-kitchen": "h-14 w-14 [&_svg]:size-6",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
